/**
 * Natural Language Query Service
 *
 * Parses natural language queries and converts them to structured
 * search parameters for the Records Management System.
 */

export interface ParsedQuery {
  intent: QueryIntent;
  entityType: EntityType;
  filters: QueryFilter[];
  timeRange?: TimeRange;
  aggregation?: AggregationType;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  confidence: number;
  originalQuery: string;
  suggestedQuery?: string;
}

export type QueryIntent =
  | 'search'
  | 'count'
  | 'list'
  | 'find'
  | 'show'
  | 'report'
  | 'compare'
  | 'analyze';

export type EntityType =
  | 'pfile'
  | 'staff'
  | 'document'
  | 'jacket'
  | 'transfer'
  | 'promotion'
  | 'department'
  | 'mda';

export type AggregationType =
  | 'count'
  | 'sum'
  | 'average'
  | 'min'
  | 'max'
  | 'group';

export interface QueryFilter {
  field: string;
  operator: FilterOperator;
  value: string | number | boolean | Date;
  displayValue?: string;
}

export type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'between'
  | 'in'
  | 'is_null'
  | 'is_not_null';

export interface TimeRange {
  start?: Date;
  end?: Date;
  relative?: string; // "last 30 days", "this year", etc.
}

// Keyword mappings for entity detection
const ENTITY_KEYWORDS: Record<EntityType, string[]> = {
  pfile: ['pfile', 'pfiles', 'file', 'files', 'jacket', 'jackets', 'personnel file', 'personnel files', 'record', 'records'],
  staff: ['staff', 'officer', 'officers', 'employee', 'employees', 'civil servant', 'civil servants', 'worker', 'workers'],
  document: ['document', 'documents', 'letter', 'letters', 'certificate', 'certificates', 'memo', 'memos'],
  jacket: ['jacket', 'jackets', 'folder', 'folders'],
  transfer: ['transfer', 'transfers', 'posting', 'postings', 'secondment', 'secondments'],
  promotion: ['promotion', 'promotions', 'advancement', 'upgrade', 'upgrades'],
  department: ['department', 'departments', 'unit', 'units', 'division', 'divisions'],
  mda: ['mda', 'mdas', 'ministry', 'ministries', 'agency', 'agencies'],
};

// Intent keywords
const INTENT_KEYWORDS: Record<QueryIntent, string[]> = {
  search: ['search', 'find', 'look for', 'locate', 'where is', 'where are'],
  count: ['count', 'how many', 'number of', 'total'],
  list: ['list', 'show', 'display', 'get', 'fetch', 'retrieve'],
  find: ['find', 'get', 'locate', 'identify'],
  show: ['show', 'display', 'view', 'see'],
  report: ['report', 'summary', 'summarize', 'overview'],
  compare: ['compare', 'difference', 'versus', 'vs'],
  analyze: ['analyze', 'analysis', 'trend', 'pattern'],
};

// Time-related keywords
const TIME_PATTERNS: Record<string, () => TimeRange> = {
  'today': () => ({ start: startOfDay(new Date()), end: new Date() }),
  'yesterday': () => {
    const yesterday = subDays(new Date(), 1);
    return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
  },
  'this week': () => ({ relative: 'this week', start: startOfWeek(new Date()), end: new Date() }),
  'last week': () => {
    const lastWeek = subDays(new Date(), 7);
    return { relative: 'last week', start: startOfWeek(lastWeek), end: endOfWeek(lastWeek) };
  },
  'this month': () => ({ relative: 'this month', start: startOfMonth(new Date()), end: new Date() }),
  'last month': () => {
    const lastMonth = subMonths(new Date(), 1);
    return { relative: 'last month', start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
  },
  'this year': () => ({ relative: 'this year', start: startOfYear(new Date()), end: new Date() }),
  'last year': () => {
    const lastYear = subYears(new Date(), 1);
    return { relative: 'last year', start: startOfYear(lastYear), end: endOfYear(lastYear) };
  },
  'last 7 days': () => ({ relative: 'last 7 days', start: subDays(new Date(), 7), end: new Date() }),
  'last 30 days': () => ({ relative: 'last 30 days', start: subDays(new Date(), 30), end: new Date() }),
  'last 90 days': () => ({ relative: 'last 90 days', start: subDays(new Date(), 90), end: new Date() }),
};

// Date utility functions (simplified - in production use date-fns)
function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return startOfDay(d);
}

function endOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (6 - day));
  return endOfDay(d);
}

function startOfMonth(date: Date): Date {
  const d = new Date(date);
  d.setDate(1);
  return startOfDay(d);
}

function endOfMonth(date: Date): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1, 0);
  return endOfDay(d);
}

function startOfYear(date: Date): Date {
  const d = new Date(date);
  d.setMonth(0, 1);
  return startOfDay(d);
}

function endOfYear(date: Date): Date {
  const d = new Date(date);
  d.setMonth(11, 31);
  return endOfDay(d);
}

function subDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
}

function subMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
}

function subYears(date: Date, years: number): Date {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() - years);
  return d;
}

class NaturalLanguageQueryService {
  /**
   * Parse a natural language query into structured search parameters
   */
  parseQuery(query: string): ParsedQuery {
    const normalizedQuery = query.toLowerCase().trim();
    let confidence = 0;

    // Detect intent
    const intent = this.detectIntent(normalizedQuery);
    confidence += intent.confidence;

    // Detect entity type
    const entityType = this.detectEntityType(normalizedQuery);
    confidence += entityType.confidence;

    // Extract filters
    const filters = this.extractFilters(normalizedQuery);
    confidence += filters.length > 0 ? 20 : 0;

    // Extract time range
    const timeRange = this.extractTimeRange(normalizedQuery);
    confidence += timeRange ? 15 : 0;

    // Detect aggregation
    const aggregation = this.detectAggregation(normalizedQuery);
    confidence += aggregation ? 10 : 0;

    // Extract limit
    const limit = this.extractLimit(normalizedQuery);

    // Detect sort
    const { sortBy, sortOrder } = this.detectSort(normalizedQuery);

    // Normalize confidence to 0-100
    confidence = Math.min(100, confidence);

    return {
      intent: intent.value,
      entityType: entityType.value,
      filters: filters,
      timeRange,
      aggregation,
      limit,
      sortBy,
      sortOrder,
      confidence,
      originalQuery: query,
      suggestedQuery: this.generateSuggestedQuery(intent.value, entityType.value, filters),
    };
  }

  /**
   * Detect query intent
   */
  private detectIntent(query: string): { value: QueryIntent; confidence: number } {
    for (const [intent, keywords] of Object.entries(INTENT_KEYWORDS)) {
      for (const keyword of keywords) {
        if (query.includes(keyword)) {
          return { value: intent as QueryIntent, confidence: 25 };
        }
      }
    }
    // Default to 'search' if no intent detected
    return { value: 'search', confidence: 10 };
  }

  /**
   * Detect entity type being queried
   */
  private detectEntityType(query: string): { value: EntityType; confidence: number } {
    for (const [entity, keywords] of Object.entries(ENTITY_KEYWORDS)) {
      for (const keyword of keywords) {
        if (query.includes(keyword)) {
          return { value: entity as EntityType, confidence: 25 };
        }
      }
    }
    // Default to 'pfile' for records system
    return { value: 'pfile', confidence: 10 };
  }

  /**
   * Extract filters from query
   */
  private extractFilters(query: string): QueryFilter[] {
    const filters: QueryFilter[] = [];

    // Check for grade mentions
    const gradeMatch = query.match(/grade\s*(\d+)/i);
    if (gradeMatch) {
      filters.push({
        field: 'grade',
        operator: 'equals',
        value: parseInt(gradeMatch[1]),
        displayValue: `Grade ${gradeMatch[1]}`,
      });
    }

    // Check for "checked out" status
    if (query.includes('checked out') || query.includes('checkout')) {
      const isCheckedOut = !query.includes('not checked out');
      filters.push({
        field: 'isCheckedOut',
        operator: 'equals',
        value: isCheckedOut,
        displayValue: isCheckedOut ? 'Checked out' : 'Not checked out',
      });
    }

    // Check for "pending" or status
    if (query.includes('pending')) {
      filters.push({
        field: 'status',
        operator: 'equals',
        value: 'pending',
        displayValue: 'Pending status',
      });
    }

    if (query.includes('active')) {
      filters.push({
        field: 'status',
        operator: 'equals',
        value: 'active',
        displayValue: 'Active status',
      });
    }

    // Check for "incomplete" or "missing"
    if (query.includes('incomplete') || query.includes('missing')) {
      filters.push({
        field: 'isComplete',
        operator: 'equals',
        value: false,
        displayValue: 'Incomplete records',
      });
    }

    // Check for retirement mentions
    if (query.includes('retiring') || query.includes('retirement')) {
      const yearMatch = query.match(/in\s*(\d{4})/);
      if (yearMatch) {
        filters.push({
          field: 'retirementYear',
          operator: 'equals',
          value: parseInt(yearMatch[1]),
          displayValue: `Retiring in ${yearMatch[1]}`,
        });
      } else if (query.includes('approaching') || query.includes('soon')) {
        filters.push({
          field: 'retirementDate',
          operator: 'less_than',
          value: subMonths(new Date(), -12), // Within next 12 months
          displayValue: 'Retiring within 12 months',
        });
      }
    }

    // Check for "more than X days"
    const daysMatch = query.match(/more than (\d+) days/i);
    if (daysMatch && query.includes('checked out')) {
      filters.push({
        field: 'checkoutDuration',
        operator: 'greater_than',
        value: parseInt(daysMatch[1]),
        displayValue: `Checked out > ${daysMatch[1]} days`,
      });
    }

    // Check for MDA mentions
    const mdaPatterns = ['ohcs', 'moh', 'moe', 'mof', 'mod'];
    for (const mda of mdaPatterns) {
      if (query.includes(mda)) {
        filters.push({
          field: 'mdaCode',
          operator: 'equals',
          value: mda.toUpperCase(),
          displayValue: `MDA: ${mda.toUpperCase()}`,
        });
        break;
      }
    }

    return filters;
  }

  /**
   * Extract time range from query
   */
  private extractTimeRange(query: string): TimeRange | undefined {
    for (const [pattern, generator] of Object.entries(TIME_PATTERNS)) {
      if (query.includes(pattern)) {
        return generator();
      }
    }

    // Check for "in YYYY" pattern
    const yearMatch = query.match(/in (\d{4})/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1]);
      return {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31),
        relative: `in ${year}`,
      };
    }

    return undefined;
  }

  /**
   * Detect aggregation type
   */
  private detectAggregation(query: string): AggregationType | undefined {
    if (query.includes('how many') || query.includes('count') || query.includes('total number')) {
      return 'count';
    }
    if (query.includes('average') || query.includes('avg')) {
      return 'average';
    }
    if (query.includes('group by') || query.includes('by department') || query.includes('by mda')) {
      return 'group';
    }
    return undefined;
  }

  /**
   * Extract limit from query
   */
  private extractLimit(query: string): number | undefined {
    const limitMatch = query.match(/(?:top|first|limit)\s*(\d+)/i);
    if (limitMatch) {
      return parseInt(limitMatch[1]);
    }
    return undefined;
  }

  /**
   * Detect sort parameters
   */
  private detectSort(query: string): { sortBy?: string; sortOrder?: 'asc' | 'desc' } {
    let sortBy: string | undefined;
    let sortOrder: 'asc' | 'desc' | undefined;

    if (query.includes('oldest') || query.includes('earliest')) {
      sortBy = 'createdAt';
      sortOrder = 'asc';
    } else if (query.includes('newest') || query.includes('latest') || query.includes('recent')) {
      sortBy = 'createdAt';
      sortOrder = 'desc';
    } else if (query.includes('alphabetical') || query.includes('a-z')) {
      sortBy = 'staffName';
      sortOrder = 'asc';
    }

    return { sortBy, sortOrder };
  }

  /**
   * Generate a suggested query based on parsed parameters
   */
  private generateSuggestedQuery(intent: QueryIntent, entityType: EntityType, filters: QueryFilter[]): string {
    const parts: string[] = [];

    // Intent
    switch (intent) {
      case 'count':
        parts.push('Count');
        break;
      case 'list':
      case 'show':
        parts.push('Show all');
        break;
      default:
        parts.push('Find');
    }

    // Entity
    parts.push(entityType === 'pfile' ? 'personnel files' : entityType + 's');

    // Filters
    if (filters.length > 0) {
      parts.push('where');
      parts.push(filters.map(f => f.displayValue || `${f.field} ${f.operator} ${f.value}`).join(' and '));
    }

    return parts.join(' ');
  }

  /**
   * Get query suggestions based on partial input
   */
  getSuggestions(partialQuery: string): string[] {
    const suggestions: string[] = [];
    const normalized = partialQuery.toLowerCase();

    // Common query templates
    const templates = [
      'Show all PFiles checked out for more than 30 days',
      'Find staff approaching retirement in 2025',
      'List incomplete personnel files',
      'Count all Grade 14 officers in OHCS',
      'Show transfers pending approval',
      'Find files with missing documents',
      'List recent promotions this year',
      'Show staff by department',
    ];

    for (const template of templates) {
      if (template.toLowerCase().includes(normalized) || normalized === '') {
        suggestions.push(template);
      }
    }

    return suggestions.slice(0, 5);
  }
}

// Export singleton instance
export const nlQueryService = new NaturalLanguageQueryService();

export type { NaturalLanguageQueryService };
